import { useState } from "react";
import { Check, ChevronLeft, Lock } from "lucide-react";
import { Button } from "@/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/dialog";
import { ROUND, type SelectionStage } from "./CustomerGalleryControls";

// Thanh hành động NỔI ở đáy màn — trình bày lại theo SubmitBar của dự án PHOTO (CEO chốt
// 27/08/2026). Trước đây nút "Chốt danh sách cuối" nằm tít cuối trang (phải cuộn hết mới thấy)
// và bấm là chốt LUÔN không hỏi lại — chốt xong là khoá, rất nguy hiểm.
//
// Bốn luật mang từ PHOTO sang:
//  1. Nút nổi `fixed` đáy màn, LUÔN nhìn thấy. Lớp ngoài `pointer-events-none` để dải trong suốt
//     hai bên nút không nuốt cú chạm vào ảnh hàng cuối; trang chừa sẵn pb cho nút (xem trang).
//  2. Nút MỜ vẫn phải ĐỌC ĐƯỢC lý do: "Chưa chọn ảnh nào cho danh sách cuối" — disabled chứ
//     không ẩn đi.
//  3. Chữ trên nút nói đúng việc sắp xảy ra, kèm con số: "Chốt danh sách cuối (12)".
//  4. Trước khi chốt thật có HỘP XÁC NHẬN nói rõ hậu quả (khoá danh sách); hộp đứng yên trong
//     lúc chờ máy chủ, chỉ đóng khi biết chắc kết quả.
//
// Vòng 1 là vòng làm việc tự do — nút chính ở vòng ấy chỉ chuyển sang vòng 2, không gọi mạng,
// nên không cần xác nhận. Số ảnh mỗi vòng không bị giới hạn.

export function SelectionBar({
  stage,
  draftCount,
  finalCount,
  locked,
  saving,
  onStageChange,
  onCommit,
}: {
  stage: SelectionStage;
  draftCount: number;
  finalCount: number;
  locked: boolean;
  saving: boolean;
  onStageChange: (stage: SelectionStage) => void;
  onCommit: () => Promise<void>;
}) {
  const [confirming, setConfirming] = useState(false);

  if (locked) {
    return (
      <div
        aria-label="Chốt lựa chọn ảnh"
        role="region"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
      >
        <p className="pointer-events-auto flex items-center gap-2 rounded-full border bg-background/95 px-5 py-3 text-sm shadow-lg backdrop-blur">
          <Lock className="h-4 w-4" aria-hidden />
          <strong>Danh sách đã chốt</strong>
          <span className="text-muted-foreground">
            {ROUND.draft.short} {draftCount} · {ROUND.final.short} {finalCount}
          </span>
        </p>
      </div>
    );
  }

  return (
    <div
      aria-label="Chốt lựa chọn ảnh"
      role="region"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex flex-col items-center gap-1 px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
    >
      <div className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-2 rounded-full border bg-background/95 py-2 pl-4 pr-2 shadow-lg backdrop-blur">
        {stage === "draft" ? (
          <>
            <span className="whitespace-nowrap text-sm">
              {ROUND.draft.short} <strong className="tabular-nums">{draftCount}</strong>
            </span>
            <Button type="button" className="min-h-11 rounded-full" onClick={() => onStageChange("final")}>
              Sang {ROUND.final.short}
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="min-h-11 rounded-full"
              onClick={() => onStageChange("draft")}
            >
              <ChevronLeft /> {ROUND.draft.short}
            </Button>
            <span className="whitespace-nowrap text-sm">
              {ROUND.final.short} <strong className="tabular-nums">{finalCount}</strong>
            </span>
            <Button
              type="button"
              className="min-h-11 rounded-full"
              disabled={saving || finalCount === 0}
              onClick={() => setConfirming(true)}
            >
              <Check /> Chốt danh sách ({finalCount})
            </Button>
          </>
        )}
      </div>
      {stage === "final" && finalCount === 0 ? (
        <p className="pointer-events-auto rounded-full bg-background/90 px-3 py-1 text-xs text-muted-foreground shadow">
          Chưa chọn ảnh nào để chốt — bấm ♥ trên ảnh để chọn
        </p>
      ) : null}

      <Dialog open={confirming} onOpenChange={(open) => !saving && setConfirming(open)}>
        <DialogContent className="pointer-events-auto sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Chốt {finalCount} ảnh?</DialogTitle>
            <DialogDescription>
              Sau khi chốt, danh sách sẽ khoá để studio bắt đầu xử lý — bạn không đổi lựa chọn
              được nữa.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={saving}
              onClick={() => setConfirming(false)}
            >
              Quay lại
            </Button>
            <Button
              type="button"
              disabled={saving}
              onClick={() => {
                void onCommit().then(
                  () => setConfirming(false),
                  () => setConfirming(false),
                );
              }}
            >
              {saving ? "Đang chốt…" : "Chốt danh sách"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
